import type React from "react";
import { useEffect } from "react";
import type { DataSet, Node as VisNode } from "vis-network/standalone";
import type { GraphTheme } from "@/features/graph/hooks/useGraphTheme";
import { shiftHexColor } from "@/features/graph/hooks/useGraphTheme";
import type { VisualNode } from "@/types/knowledgeGraph";

interface UseGraphSelectionSyncOptions {
	selectedNodeId?: string | null;
	filteredNodes: VisualNode[];
	nodesDataSetRef: React.MutableRefObject<DataSet<VisNode> | null>;
	nodeDisplayColors: Map<string, string>;
	graphTheme: GraphTheme;
	theme: "light" | "dark";
}

export const useGraphSelectionSync = ({
	selectedNodeId,
	filteredNodes,
	nodesDataSetRef,
	nodeDisplayColors,
	graphTheme,
	theme,
}: UseGraphSelectionSyncOptions): void => {
	useEffect(() => {
		if (!nodesDataSetRef.current || filteredNodes.length === 0) return;

		filteredNodes.forEach((node) => {
			const isSelected = selectedNodeId === node.id;
			const nodeTone = nodeDisplayColors.get(node.id) || node.color || graphTheme.accent;

			nodesDataSetRef.current?.update({
				id: node.id,
				color: {
					background: isSelected ? graphTheme.accentSoft : nodeTone,
					border: isSelected ? graphTheme.accent : graphTheme.borderStrong,
					highlight: {
						background: graphTheme.accentSoft,
						border: graphTheme.accent,
					},
					hover: {
						background: shiftHexColor(nodeTone, 0.08),
						border: graphTheme.accent,
					},
				},
				borderWidth: isSelected ? 4 : 2.5,
				shadow: {
					enabled: true,
					color: isSelected ? "rgba(255, 68, 27, 0.35)" : theme === "dark" ? "rgba(0,0,0,0.45)" : "rgba(0,0,0,0.15)",
					size: isSelected ? 12 : 8,
					x: 2,
					y: 2,
				},
			});
		});
	}, [selectedNodeId, filteredNodes, nodesDataSetRef, nodeDisplayColors, graphTheme, theme]);
};

export default useGraphSelectionSync;
